import React, {
  useState,
  useContext,
  createContext,
  useEffect,
  ReactChildren,
  ReactChild,
  PropsWithChildren,
} from 'react'
import { Cents, PaymentAdjustment, Payment } from '../model-payments'

export interface PaginatedResult {
  total: number
  pages: number
  page: number
  per_page: number
}

type BillingPayloadType =
  | 'delivery order'
  | 'pickup order'
  | 'curbside order'
  | 'dinein order'
  | 'chargeback'
  | 'chargeback success'
  | 'credit restaurant'
type BillableType = 'order' | 'fax'

export interface Transaction {
  id: number
  amount: Cents
  billing_rule_name: string
  credit_account_id: number
  debit_account_id: number
  applies_to: BillingPayloadType
  description: string
  billable_id: string
  billable_type: BillableType
  created_at: string
  invoice_id: number | null
  highlight?: boolean
}

export interface TransactionList {
  transactions: Transaction[]
  pagination: PaginatedResult
  nextPage?: () => Promise<TransactionList>
}

type OwnerType = 'subscription' | 'restaurant' | 'zuppler' | 'customer'
type AccountType = 'principal' | 'balance'

export interface BalanceAccount {
  owner_id: number
  owner_type: OwnerType
  type: AccountType
  name: string
  credit: Cents
  debit: Cents
  total: Cents
}

export interface Account {
  id: number
  description: string
  name: string
  owner_id: number
  owner_type: OwnerType
  type: AccountType
}

export interface PaymentsAPI {
  findOrderTransactions: (orderId: string) => Promise<TransactionList>
  findInvoiceTransactions: (invoiceableId: number, page?: number, perPage?: number) => Promise<TransactionList>
  findInvoiceableTransactions: (
    invoiceId: number,
    invoiceableId: string,
    invoiceableType: string,
    page?: number,
    perPage?: number
  ) => Promise<TransactionList>
  getOrderBalance: (
    orderId: string,
    accounts: Pick<BalanceAccount, 'owner_id' | 'owner_type'>[]
  ) => Promise<BalanceAccount[]>
  loadAccount: (accountId: number) => Promise<Account>
  createAdjustment: (adj: PaymentAdjustment) => Promise<Payment>
  createFullRefund: (billingOrderId: number) => Promise<void>
  createFullRefundChargeProvider: (billingOrderId: number) => Promise<void>
  createTransaction: (
    billableType: BillableType,
    billableId: string,
    debitAccount: Account,
    creditAccount: Account,
    amount: number,
    description: string,
    appliesTo: BillingPayloadType,
    billingRuleName: string
  ) => Promise<Transaction>
  createNamedTransactions: (order_uuid: string, billing_rules_names: string[]) => Promise<{ transactions: Transaction[] }>
}

const authJsonHeaders = (token: string) => {
  return {
    'Content-Type': 'application/json',
    Authorisation: `Bearer ${token}`,
    Authorization: `Bearer ${token}`,
  }
}

const PaymentsAPICtx = createContext<PaymentsAPI | null>(null)

function orderTransactionsUrl() {
  return `${PAYMENTS_SVC_API2}/v5/transactions`
}

function balanceTransactionsUrl() {
  return `${PAYMENTS_SVC_API2}/v5/transactions/balance`
}

function accountUrl(accountId: number): string {
  return `${PAYMENTS_SVC_API2}/v5/accounts/${accountId}`
}

function paymentAdjustmentsUrl(paymentId: number): string {
  return `${PAYMENTS_SVC_API2}/v5/payments/${paymentId}/adjustments`
}

function fullRefundUrl(billingOrderId: number): string {
  return `${PAYMENTS_SVC_API2}/v5/orders/${billingOrderId}/refund`
}
function fullRefundChargeProviderUrl(billingOrderId: number): string {
  return `${PAYMENTS_SVC_API2}/v5/orders/${billingOrderId}/refund_and_charge_providers`
}
function createTransactionUrl(): string {
  return `${PAYMENTS_SVC_API2}/v5/create_transaction`
}
function createNamedTransactionsUrl(): string {
  return `${PAYMENTS_SVC_API2}/v5/create_named_transactions`
}

export interface AccountsCache {
  [id: string]: Account
}
export function PaymentsApi(props: PropsWithChildren<{ auth_token: string }>): JSX.Element {
  const token = props.auth_token
  const ACCOUNT_CACHE: AccountsCache = {}
  if (!token) return <>Loading...</>

  const api: PaymentsAPI = {
    findInvoiceTransactions: (invoiceId: number, page = 1, perPage = 25): Promise<TransactionList> => {
      return fetch(orderTransactionsUrl(), {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify({ invoice_id: invoiceId, page: page, per_page: perPage }),
      })
        .then((res) => res.json())
        .then((data: any): Promise<TransactionList> => {
          return Promise.resolve({
            ...data,
            nextPage: () => {
              return api.findInvoiceTransactions(invoiceId, invoiceableType, page + 1, perPage)
            },
          })
        })
    },
    findInvoiceableTransactions: (
      invoiceId: number,
      invoiceableId: string,
      invoiceableType: string,
      page = 1,
      per_page = 25
    ): Promise<TransactionList> => {
      return fetch(orderTransactionsUrl(), {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify({
          invoice_id: invoiceId,
          billable_id: invoiceableId,
          billable_type: invoiceableType,
          page,
          per_page,
        }),
      })
        .then((res) => res.json())
        .then((data: any): Promise<TransactionList> => {
          console.log('findInvoiceableTransactions', data)
          return Promise.resolve({
            ...data,
            nextPage: () => {
              return api.findInvoiceableTransactions(invoiceId, invoiceableId, invoiceableType, page + 1, per_page)
            },
          })
        })
    },
    findOrderTransactions: (orderId: string): Promise<TransactionList> => {
      return fetch(orderTransactionsUrl(), {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify({ billable_id: orderId, billable_type: 'order', page: 1, per_page: 100 }),
      })
        .then((res) => res.json())
        .then((data: any): Promise<TransactionList> => {
          return Promise.resolve({
            ...data,
            nextPage: () => {
              return Promise.reject('No more data')
            },
          })
        })
    },
    getOrderBalance: (
      orderId: string,
      accounts: Pick<BalanceAccount, 'owner_id' | 'owner_type'>[]
    ): Promise<BalanceAccount[]> => {
      return fetch(balanceTransactionsUrl(), {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify({
          billable_id: orderId,
          billable_type: 'order',
          accounts: accounts,
        }),
      }).then((res) => res.json())
    },
    loadAccount: (accountId: number): Promise<Account> => {
      if (ACCOUNT_CACHE['' + accountId]) return Promise.resolve(ACCOUNT_CACHE['' + accountId])
      else
        return fetch(accountUrl(accountId), { method: 'GET', headers: authJsonHeaders(token) })
          .then((res) => res.json())
          .then((a: Account) => {
            ACCOUNT_CACHE['' + a.id] = a
            return Promise.resolve(a)
          })
    },
    createAdjustment: (adj: PaymentAdjustment): Promise<Payment> => {
      const adjustments = [
        { field: 'tip', value: adj.tip },
        { field: 'delivery', value: adj.delivery },
        { field: 'subtotal', value: adj.subtotal },
      ]
      const payload = {
        adjustments: adjustments.filter(({ value }) => value != 0),
        note: adj.notes,
      }
      return fetch(paymentAdjustmentsUrl(adj.parentPaymentId), {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify(payload),
      }).then((res) => res.json())
    },
    createFullRefund: (billingOrderId: number): Promise<void> => {
      return fetch(fullRefundUrl(billingOrderId), {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify({}),
      }).then((res) => res.json())
    },
    createFullRefundChargeProvider: (billingOrderId: number): Promise<void> => {
      return fetch(fullRefundChargeProviderUrl(billingOrderId), {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify({}),
      }).then((res) => res.json())
    },
    createTransaction: (
      billableType: BillableType,
      billableId: string,
      debitAccount: Account,
      creditAccount: Account,
      amount: number,
      description: string,
      appliesTo: BillingPayloadType,
      billingRuleName: string
    ): Promise<Transaction> => {
      const payload = {
        transaction: {
          debit_account_id: debitAccount.id,
          credit_account_id: creditAccount.id,
          amount: Math.round(amount * 100),
          billable_id: billableId,
          billable_type: billableType,
          payload_type: appliesTo,
          description: description,
          billing_rule_name: billingRuleName,
        },
      }
      return fetch(createTransactionUrl(), {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify(payload),
      }).then((res) => res.json())
    },
    createNamedTransactions: (order_uuid: string, billing_rules_names: string[]): Promise<Transaction[]> => {
      return fetch(createNamedTransactionsUrl(), {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify({ order_uuid, billing_rules_names }),
      }).then((res) => res.json())
    },
  }
  return <PaymentsAPICtx.Provider value={api}>{props.children}</PaymentsAPICtx.Provider>
}

export function usePaymentsAPI(): PaymentsAPI {
  const ctx = useContext(PaymentsAPICtx)
  return ctx as PaymentsAPI
}
